import { useQuery } from '@tanstack/react-query'
import { Snowflake } from 'lucide-react'

import { api } from '@/lib/api'

/**
 * Story 5.7 §AC #4 + Story 5.8 — `Cold Rate` card.
 *
 * Consumes the `GET /admin/dashboard/cold-rate` endpoint added by Story 5.7
 * (RBAC: admin + manager). Displays the percentage of conversations that
 * transitioned to `cold` in the last 7 days, with a tooltip-friendly
 * "X de Y" detail in the footer.
 *
 * Empty-state convention mirrors the kpis cards (Story 4.7b): when
 * `totalCount === 0`, the API returns `percentage: null` and we render
 * "N/A" + microcopy — never `0%`, since 0% over 0 isn't meaningful.
 */
interface ColdRateResponse {
  window: { days: number; from: string; to: string }
  coldCount: number
  totalCount: number
  percentage: number | null
}

export function ColdRateCard(): React.ReactElement {
  const { data, isLoading, isError } = useQuery<ColdRateResponse>({
    queryKey: ['admin-dashboard-cold-rate'],
    queryFn: async () => api<ColdRateResponse>('/admin/dashboard/cold-rate'),
    staleTime: 60_000,
  })

  return (
    <div className="flex flex-col gap-1 rounded-lg border border-t-2 border-t-violet-500 bg-card p-3 shadow-sm transition-shadow hover:shadow-sm">
      <div className="flex items-center justify-between gap-1">
        <span className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Taxa de Cold
        </span>
        <Snowflake className="h-3.5 w-3.5 shrink-0 text-violet-500" aria-hidden="true" />
      </div>
      <div>
        {isLoading ? (
          <span className="block h-6 w-14 animate-pulse rounded bg-muted" />
        ) : isError || data === undefined ? (
          <p className="text-sm text-destructive">Erro ao carregar</p>
        ) : typeof data.percentage !== 'number' ? (
          <p className="text-2xl font-semibold leading-none tabular-nums">N/A</p>
        ) : (
          <p className="text-2xl font-semibold leading-none tabular-nums">
            {data.percentage.toFixed(1)}%
          </p>
        )}
      </div>
      <p className="truncate text-[11px] text-muted-foreground">
        {data !== undefined
          ? (data.totalCount ?? 0) === 0
            ? `Sem conversas nos últimos ${data.window?.days ?? 7}d`
            : `${data.coldCount} cold de ${data.totalCount} conversas (${data.window?.days ?? 7}d)`
          : ' '}
      </p>
    </div>
  )
}

export default ColdRateCard
